/**
 * Route chunks carry a content hash and a deploy removes the previous set. A tab
 * left open across a deploy still runs the old entry bundle, so the first
 * navigation to a route it had not visited yet asks for a file the server no
 * longer serves. The import rejects, nothing catches it, and the whole app
 * unmounts into the root error boundary — a blank page for a user who did
 * nothing wrong (GLASSTAPE-FRONT-H on /alerts, GLASSTAPE-FRONT-G on /borrowers).
 *
 * Recovery is two steps. Retry once, which covers a plain network blip and costs
 * a few hundred milliseconds. If it still fails the chunk is gone, so reload:
 * the browser fetches the current index.html and with it the hashes that exist
 * now. The reload is stamped in sessionStorage, and a second failure inside the
 * cooldown means the asset is genuinely unreachable (origin down, not a deploy)
 * — there we rethrow and let the boundary show, rather than loop.
 */

import { lazy, type ComponentType } from "react";
import * as Sentry from "@sentry/react";

const RELOAD_STAMP = "chunk_reload_at";
const RELOAD_COOLDOWN_MS = 10000;
const RETRY_DELAY_MS = 300;

// Every engine words this differently: Chrome/Edge, Firefox, Safari, and Vite's
// own CSS preloader in that order.
const CHUNK_ERROR =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i;

function isChunkError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? "");
  return CHUNK_ERROR.test(message);
}

/** True when reloading is unsafe: we already did it, or we cannot tell. */
function reloadBlocked(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_STAMP) ?? 0);
    return at > 0 && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    return true; // no storage means no loop guard, so do not reload at all
  }
}

function stampReload(): void {
  try {
    sessionStorage.setItem(RELOAD_STAMP, String(Date.now()));
  } catch {
    // Unreachable: reloadBlocked() already refused when storage throws.
  }
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Drop-in replacement for React.lazy() for route-level chunks.
 * Same signature, same return value — only the failure path differs.
 */
export function lazyRoute<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkError(err)) throw err;

      await wait(RETRY_DELAY_MS);
      try {
        return await factory();
      } catch (retryErr) {
        if (!isChunkError(retryErr) || reloadBlocked()) throw retryErr;

        stampReload();
        // Recorded as a warning, not an error: the user is about to be served
        // the page correctly, but a spike here means deploys are stranding
        // sessions more often than they should.
        Sentry.captureException(retryErr, {
          level: "warning",
          tags: { chunk_reload: "true" },
        });
        await Sentry.flush(1500);
        window.location.reload();

        // reload() does not stop execution. Returning a promise that never
        // settles keeps Suspense on its fallback until the document goes away,
        // instead of flashing the error boundary on the way out.
        return await new Promise<never>(() => {});
      }
    }
  });
}
